import React, { useState, useEffect } from 'react';
import { lookupAsinDetails, calculateRemovalCost } from '../services/amazon';
import { saveCalculationToHistory, saveEmailToDatabase } from '../services/firebase';
import './Calculator.css';
import { getAuth } from 'firebase/auth';
import EmailCollectionModal from './EmailCollectionModal';

function Calculator({ onCalculate, isRegistered, userEmail, onShowSignupModal }) {
  const [asin, setAsin] = useState('B074DZ5YVF'); // Preloaded example ASIN for dumbbells
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [policyDate, setPolicyDate] = useState('Current Rate Card');
  const [editMode, setEditMode] = useState(false);
  const [editableDimensions, setEditableDimensions] = useState('');
  const [editableWeight, setEditableWeight] = useState('');
  const [lastLookupAsin, setLastLookupAsin] = useState('');
  const [lastAsinDetails, setLastAsinDetails] = useState(null);
  const [manualDimensions, setManualDimensions] = useState('');
  const [manualWeight, setManualWeight] = useState('1.0');
  const [manualIsStandard, setManualIsStandard] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [exampleLoaded, setExampleLoaded] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasProvidedEmail, setHasProvidedEmail] = useState(false);
  const [userProvidedEmail, setUserProvidedEmail] = useState('');

  // Check authentication status
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(user => {
      setIsAuthenticated(!!user);
      if (user) {
        // If user is authenticated, they've provided their email
        setHasProvidedEmail(true);
        setUserProvidedEmail(user.email);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Load example calculation on first render
  useEffect(() => {
    if (!exampleLoaded) {
      loadExampleCalculation();
      setExampleLoaded(true);
    }
  }, [exampleLoaded]);

  const loadExampleCalculation = async () => {
    try {
      setLoadingStatus('Loading example calculation...');
      setLoading(true);
      
      // Create a mock ASIN details for 22 lb dumbbells
      const dumbellDetails = {
        title: "CAP Barbell Coated Hex Dumbbell (Example)",
        dimensions: "14 x 7 x 7 inches",
        weight: 22.0,
        isStandardSize: false, // This is an oversized item
        category: 'Sports & Fitness'
      };
      
      // Calculate removal cost
      const calculationResult = calculateRemovalCost(dumbellDetails, 1);
      
      // Set the result
      setResult(calculationResult);
      setLastAsinDetails(dumbellDetails);
      setLastLookupAsin('B074DZ5YVF');
      
    } catch (err) {
      console.error('Error loading example:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If the user is using the example ASIN, allow it without email
    if (asin === 'B074DZ5YVF' && !hasProvidedEmail) {
      // Proceed with the example calculation
    } 
    // If the user is trying to use their own ASIN but hasn't provided email
    else if (!isRegistered && !hasProvidedEmail) {
      setShowEmailModal(true);
      return; // Stop the calculation until they provide email
    }
    
    setLoading(true);
    setError('');
    setResult(null);
    
    if (isRegistered && userEmail && !isAuthenticated) {
      console.log('User is registered but not authenticated');
      onShowSignupModal();
      return;
    }
    
    try {
      onCalculate();
      
      let asinDetails;
      
      // Check if we already have details for this ASIN
      if (asin === lastLookupAsin && lastAsinDetails) {
        console.log(`📦 Reusing cached details for ASIN: ${asin}`);
        asinDetails = lastAsinDetails;
      } else {
        // Need to fetch new details
        setLoadingStatus('Looking up product information...');
        try {
          // Get ASIN details
          asinDetails = await lookupAsinDetails(asin);
          
          // Store for future use
          setLastLookupAsin(asin);
          setLastAsinDetails(asinDetails);
        } catch (err) {
          console.error('Error in ASIN lookup:', err);
          setError(err.message || 'Error looking up product information. Please check the ASIN and try again.');
          setLoading(false);
          return;
        }
      }
      
      setLoadingStatus('Calculating removal fees...');
      
      // Calculate removal cost
      const calculationResult = calculateRemovalCost(asinDetails, quantity);
      setResult(calculationResult);
      
      // Save to history if user is registered
      if (isRegistered && userEmail) {
        setLoadingStatus('Saving to your history...');
        await saveCalculationToHistory(userEmail, {
          asin,
          quantity,
          title: asinDetails.title || '',
          sizeTier: calculationResult.sizeCategory,
          dimensions: asinDetails.dimensions,
          weight: asinDetails.weight,
          volume: calculationResult.cubicFeet,
          shippingWeight: calculationResult.shippingWeight,
          dimensionalWeight: calculationResult.dimensionalWeight,
          cost: calculationResult.totalCost,
          costPerUnit: calculationResult.costPerUnit,
          date: new Date().toISOString(),
        });
      }
    } catch (err) {
      console.error('General calculation error:', err);
      setError('Error calculating removal cost. Please try again later.');
    } finally {
      setLoading(false);
      setLoadingStatus('');
    }
  };

  const handleAsinChange = (e) => {
    const newAsin = e.target.value.trim();
    setAsin(newAsin);
    
    // If they change from the example ASIN and haven't provided email, show the modal
    if (newAsin !== 'B074DZ5YVF' && !isRegistered && !hasProvidedEmail) {
      setShowEmailModal(true);
    }
  };

  const onSignupClick = () => {
    if (typeof onShowSignupModal === 'function') {
      onShowSignupModal();
    }
  };

  const handleRecalculate = () => {
    try {
      // Parse the edited dimensions and weight
      const dimensions = editableDimensions.trim();
      const weight = parseFloat(editableWeight);
      
      if (!dimensions || isNaN(weight) || weight <= 0) {
        setError('Please enter valid dimensions and weight.');
        return;
      }
      
      console.log('Original result:', result);
      console.log('Original size category:', result.sizeTier);
      
      // Create a modified ASIN details object with the updated dimensions and weight
      const modifiedDetails = {
        ...lastAsinDetails,
        dimensions: dimensions,
        weight: weight,
        isStandardSize: result.sizeTier === 'Standard size'
      };
      
      console.log('Modified details for recalculation:', modifiedDetails);
      console.log('Size category being passed:', modifiedDetails.isStandardSize ? 'Standard' : 'Oversized');
      
      // Calculate the new removal cost
      const newResult = calculateRemovalCost(modifiedDetails, quantity);
      console.log('New calculation result:', newResult);
      
      setResult(newResult);
      
      // Exit edit mode
      setEditMode(false);
    } catch (err) {
      console.error('Recalculation error:', err);
      setError('Error recalculating removal cost. Please check your inputs and try again.');
    }
  };

  const toggleEditMode = () => {
    if (!editMode) {
      // Entering edit mode - initialize with current values
      setEditableDimensions(result.dimensions);
      setEditableWeight(result.weight.toString());
      setEditMode(true);
    } else {
      // Exiting edit mode without changes
      setEditMode(false);
    }
  };

  const handleQuantityRecalculate = () => {
    if (!lastAsinDetails) {
      // If we don't have cached details, run the full calculation
      handleSubmit(new Event('submit'));
      return;
    }
    
    try {
      // Calculate removal cost with new quantity but same ASIN details
      const calculationResult = calculateRemovalCost(lastAsinDetails, quantity);
      setResult(calculationResult);
      
      // Save to history if user is registered
      if (isRegistered && userEmail) {
        saveCalculationToHistory(userEmail, {
          asin,
          quantity,
          title: lastAsinDetails.title || '',
          sizeTier: calculationResult.sizeCategory,
          dimensions: lastAsinDetails.dimensions,
          weight: lastAsinDetails.weight,
          volume: calculationResult.cubicFeet,
          shippingWeight: calculationResult.shippingWeight,
          dimensionalWeight: calculationResult.dimensionalWeight,
          cost: calculationResult.totalCost,
          costPerUnit: calculationResult.costPerUnit,
          date: new Date().toISOString(),
        });
      }
    } catch (err) {
      console.error('Error in quick recalculation:', err);
      setError('Error recalculating removal cost. Please try again.');
    }
  };

  const handleEmailSubmit = async (email, name, amazonStore) => {
    try {
      setError(''); // Clear any previous errors
      
      // The email is already saved in the EmailCollectionModal component
      console.log('Email successfully processed:', email);
      
      // Update state to reflect that the user has provided their email
      setHasProvidedEmail(true);
      setUserProvidedEmail(email);
      
      // Close the modal
      setShowEmailModal(false);
      
    } catch (error) {
      console.error('Error processing email:', error);
      throw error; // Re-throw to be caught by the EmailCollectionModal
    }
  };

  return (
    <div className="calculator-container">
      <h2 className="calculator-title">Easily Calculate Removal and Disposal Fees with automatic size data</h2>
      <p className="calculator-description">
        We help calculate exact removal order fees for your Amazon FBA inventory automatically. 
        Enter your ASIN below to get instant cost estimates for disposal or return removal orders.
      </p>
      
      <p className="calculator-note">
        <small>100% automatic and free removal cost calculator for Amazon sellers</small>
      </p>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="asin">Amazon ASIN:</label>
          <input
            id="asin"
            type="text"
            value={asin}
            onChange={handleAsinChange}
            placeholder="Enter ASIN (e.g., B07PVCVBN7)"
            required
            disabled={loading}
          />
          <small className="input-help">
            {hasProvidedEmail || asin === 'B074DZ5YVF' 
              ? "Enter the ASIN of the product you want to calculate removal fees for" 
              : "You'll need to provide your email before using your own ASIN"}
          </small>
        </div>
        
        <div className="form-group">
          <label htmlFor="quantity">Quantity:</label>
          <div className="quantity-container">
            <input
              id="quantity"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              required
              disabled={loading}
            />
            {lastAsinDetails && asin === lastLookupAsin && (
              <button 
                type="button" 
                className="quick-recalc-button"
                onClick={handleQuantityRecalculate}
                disabled={loading}
              >
                Update
              </button>
            )}
          </div>
        </div>
        
        <button type="submit" disabled={loading}>
          {loading ? 'Calculating...' : 'Calculate Removal Cost'}
        </button>
      </form>
      
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-status">{loadingStatus}</p>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
          {error.includes('API') && (
            <p className="api-troubleshooting">
              We're experiencing some issues with our product data service. 
              You can manually enter product dimensions and weight below to calculate removal fees.
            </p>
          )}
        </div>
      )}
      
      {error && error.includes('API') && !result && (
        <div className="manual-entry-form">
          <h3>Enter Product Details Manually</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const manualDetails = {
              title: `Product (${asin})`,
              dimensions: manualDimensions,
              weight: parseFloat(manualWeight),
              isStandardSize: manualIsStandard,
              category: 'Manual Entry'
            };
            const calculationResult = calculateRemovalCost(manualDetails, quantity);
            setResult(calculationResult);
          }}>
            <div className="form-group">
              <label htmlFor="manualDimensions">Dimensions (L × W × H):</label>
              <input
                id="manualDimensions"
                type="text"
                value={manualDimensions}
                onChange={(e) => setManualDimensions(e.target.value)}
                placeholder="e.g., 10 x 8 x 2"
                required
              />
              <small className="input-help">Enter only the numbers and "x" between dimensions</small>
            </div>
            <div className="form-group">
              <label htmlFor="manualWeight">Weight (lbs):</label>
              <input
                id="manualWeight"
                type="number"
                step="0.01"
                min="0.01"
                value={manualWeight}
                onChange={(e) => setManualWeight(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Size Category:</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="sizeCategory"
                    checked={manualIsStandard}
                    onChange={() => setManualIsStandard(true)}
                  />
                  Standard Size
                </label>
                <label>
                  <input
                    type="radio"
                    name="sizeCategory"
                    checked={!manualIsStandard}
                    onChange={() => setManualIsStandard(false)}
                  />
                  Large/Bulky/Special Handling
                </label>
              </div>
            </div>
            <button type="submit">Calculate with Manual Data</button>
          </form>
        </div>
      )}
      
      {result && (
        <div className="result-container">
          <h2>Removal Cost Estimate</h2>
          <p><strong>ASIN:</strong> {asin}</p>
          {result.title && <p><strong>Product:</strong> {result.title}</p>}
          <p><strong>Size Category:</strong> {result.sizeTier}</p>
          
          {!editMode ? (
            <>
              <p><strong>Dimensions:</strong> {result.sortedDimensions || result.dimensions}</p>
              <p><strong>Volume:</strong> {result.cubicFeet} cubic feet</p>
              <p><strong>Actual Weight:</strong> {result.weight} lbs</p>
              <button 
                className="edit-button" 
                onClick={toggleEditMode}
                style={{ marginTop: '10px', marginBottom: '10px' }}
              >
                Edit Dimensions & Weight
              </button>
            </>
          ) : (
            <div className="edit-dimensions-form">
              <div className="form-group">
                <label htmlFor="editDimensions">Dimensions (L × W × H):</label>
                <input
                  id="editDimensions"
                  type="text"
                  value={editableDimensions}
                  onChange={(e) => setEditableDimensions(e.target.value)}
                  placeholder="e.g., 10 x 8 x 2"
                />
                <small className="input-help">Enter only the numbers and "x" between dimensions</small>
              </div>
              <div className="form-group">
                <label htmlFor="editWeight">Weight:</label>
                <input
                  id="editWeight"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={editableWeight}
                  onChange={(e) => setEditableWeight(e.target.value)}
                />
                <small className="input-help">Enter weight in pounds</small>
              </div>
              <div className="edit-buttons">
                <button type="button" onClick={handleRecalculate}>
                  Recalculate
                </button>
                <button 
                  type="button" 
                  onClick={toggleEditMode}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          
          {result.dimensionalWeight > 0 && (
            <p><strong>Dimensional Weight:</strong> {result.dimensionalWeight} lbs</p>
          )}
          <p><strong>Shipping Weight:</strong> {result.roundedWeight} lbs (used for fee calculation)</p>
          <p><strong>Cost per Unit:</strong> ${result.costPerUnit ? result.costPerUnit.toFixed(2) : '0.00'}</p>
          <p><strong>Quantity:</strong> {quantity}</p>
          <p className="total-cost"><strong>Total Cost:</strong> ${result.totalCost ? result.totalCost.toFixed(2) : '0.00'}</p>
          <p className="dimensional-weight-note" style={{ fontSize: '0.9em', color: '#666', marginTop: '10px' }}>
            <strong>Note:</strong> Amazon uses the greater of actual weight or dimensional weight for fee calculation. 
            Dimensional weight is calculated as (Length × Width × Height in inches) ÷ 139.
          </p>
          <p className="policy-note">
            Based on <a href="https://sellercentral.amazon.com/gp/help/external/G201074400" target="_blank" rel="noopener noreferrer">
              Amazon's removal fee policy
            </a> - {result.policySource}. Fees shown are per unit and may vary based on actual measurements by Amazon.
          </p>
        </div>
      )}
      
      {/* Only show sign-up prompt if user is not registered and hasn't provided email */}
      {!isRegistered && !hasProvidedEmail && (
        <>
          <h2 className="create-account-heading">Create an Account</h2>
          <div className="signup-prompt" onClick={() => onSignupClick()}>
            <p>Sign up to save your calculation history and use advanced features!</p>
            <button className="signup-button">Sign Up Now</button>
          </div>
        </>
      )}
      
      {!result && !loading && (
        <div className="example-note">
          <p>
            <strong>Example:</strong> We've preloaded a 22 lb dumbbell (oversized item) to show you how the calculator works.
            Click "Calculate" to see the removal fee estimate, or enter your own ASIN above.
          </p>
        </div>
      )}
      
      {showEmailModal && (
        <EmailCollectionModal
          onClose={() => {
            // If they haven't provided email and try to close, reset to example ASIN
            if (!hasProvidedEmail) {
              setAsin('B074DZ5YVF');
            }
            setShowEmailModal(false);
          }}
          onSubmit={handleEmailSubmit}
          isMandatory={true}
        />
      )}
    </div>
  );
}

export default Calculator; 